import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Clock } from 'lucide-react';
import { fetchInvoiceTimeline } from '../../api/resources';
import { fmtDate } from '../../utils/format';
import Spinner from '../ui/Spinner';

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const time = d.getHours() || d.getMinutes() ? ` ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '';
  return `${fmtDate(iso)}${time}`;
};

/**
 * One order's life, step by step, so a delay can be pinned to the place it
 * happened. Steps come from what the app already records: the order's dates and
 * each job's own start / finish stamps.
 *
 * Reading it: a filled tick is done, an amber ring is where the order is
 * sitting right now, a red ring is past its date.
 */
export default function OrderTimeline({ invoiceId, compact = false }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['invoice-timeline', invoiceId],
    queryFn: () => fetchInvoiceTimeline(invoiceId),
    enabled: !!invoiceId,
  });

  if (isLoading) return <div className="py-3"><Spinner /></div>;
  if (isError || !data) return <p className="py-2 text-xs italic text-content-subtle">Could not load the timeline.</p>;

  const { steps = [], stuckAt, daysOpen, jobs = [] } = data;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-xs font-bold uppercase tracking-wider text-content">Order timeline</h3>
        <span className="text-2xs text-content-subtle">
          {daysOpen} day{daysOpen === 1 ? '' : 's'} since booking
          {stuckAt && <> · waiting at <b className="text-content-muted">{steps.find((s) => s.key === stuckAt)?.label || stuckAt}</b></>}
        </span>
      </div>

      {/* One row across the page. On a narrow screen it scrolls sideways
          rather than stacking, so the order of the steps always reads the same. */}
      <ol className="flex w-full items-start gap-0 overflow-x-auto pb-1">
        {steps.map((s, i) => {
          const current = s.key === stuckAt;
          const tone = s.done
            ? { dot: 'bg-success-600 text-white border-success-600', text: 'text-content', line: 'bg-success-500' }
            : s.late
              ? { dot: 'border-2 border-danger-500 bg-surface text-danger-600', text: 'text-danger-600', line: 'bg-line' }
              : current
                ? { dot: 'border-2 border-warning-500 bg-surface text-warning-700', text: 'text-content', line: 'bg-line' }
                : { dot: 'border-2 border-line bg-surface text-content-subtle', text: 'text-content-subtle', line: 'bg-line' };
          const Icon = s.done ? Check : s.late ? AlertTriangle : Clock;
          const prevDone = i > 0 && steps[i - 1].done;
          return (
            <li key={s.key} className="relative flex min-w-[8.5rem] flex-1 flex-col items-center px-1 text-center">
              {/* the road, drawn either side of the dot */}
              {i > 0 && <span className={`absolute left-0 top-[0.7rem] h-0.5 w-1/2 ${prevDone ? 'bg-success-500' : 'bg-line'}`} />}
              {i < steps.length - 1 && <span className={`absolute right-0 top-[0.7rem] h-0.5 w-1/2 ${s.done ? 'bg-success-500' : 'bg-line'}`} />}

              <span className={`relative z-[1] flex h-6 w-6 items-center justify-center rounded-full ${tone.dot}`}>
                <Icon size={12} />
              </span>

              <p className={`mt-1.5 text-xs font-semibold leading-tight ${tone.text}`}>{s.label}</p>
              {s.at && <span className="text-2xs text-content-muted">{when(s.at)}</span>}
              {(current && !s.done) && (
                <span className="mt-0.5 rounded bg-warning-50 px-1.5 text-2xs font-bold text-warning-700">HERE</span>
              )}
              {s.late && (
                <span className="mt-0.5 rounded bg-danger-50 px-1.5 text-2xs font-bold text-danger-600">LATE</span>
              )}
              {(s.who || s.note) && (
                <p className="mt-0.5 text-2xs leading-tight text-content-subtle" title={[s.who, s.note].filter(Boolean).join(' · ')}>
                  {[s.who, s.note].filter(Boolean).join(' · ')}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {!compact && jobs.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-md border border-line">
          <table className="w-full text-xs">
            <thead className="bg-surface-sunken text-left text-2xs font-bold uppercase tracking-wide text-content-muted">
              <tr>
                <th className="px-2 py-1.5">Garment</th>
                <th className="px-2 py-1.5">Work</th>
                <th className="px-2 py-1.5">Worker</th>
                <th className="px-2 py-1.5">Started</th>
                <th className="px-2 py-1.5">Finished</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((jb, i) => (
                <tr key={i} className="border-t border-line">
                  <td className="px-2 py-1">{jb.itemName}</td>
                  <td className="px-2 py-1">{jb.workType === 'CUTTING' ? 'Cutting' : 'Stitching'}</td>
                  <td className="px-2 py-1">{jb.worker || <span className="text-content-subtle">not assigned</span>}</td>
                  <td className="px-2 py-1 text-content-muted">{when(jb.startedAt) || '—'}</td>
                  <td className="px-2 py-1 text-content-muted">{when(jb.completedAt) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
